import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight, ShieldCheck, Sparkles, Activity } from "lucide-react";

const HERO_BG = "https://images.pexels.com/photos/29948395/pexels-photo-29948395.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1600";
const HERO_DOCTOR = "https://images.unsplash.com/photo-1666886573212-2de95596d509?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODF8MHwxfHNlYXJjaHwyfHxkb2N0b3IlMjB1c2luZyUyMGRpZ2l0YWwlMjB0YWJsZXR8ZW58MHx8fHwxNzc4MzcxMzM0fDA&ixlib=rb-4.1.0&q=85";
const HERO_WARD = "https://images.pexels.com/photos/13697732/pexels-photo-13697732.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });

  const yBg = useTransform(scrollYProgress, [0, 1], ["0%", "30%"]);
  const yDoctor = useTransform(scrollYProgress, [0, 1], ["0%", "-25%"]);
  const yWard = useTransform(scrollYProgress, [0, 1], ["0%", "-15%"]);
  const yChip1 = useTransform(scrollYProgress, [0, 1], ["0%", "-60%"]);
  const yChip2 = useTransform(scrollYProgress, [0, 1], ["0%", "-90%"]);
  const titleY = useTransform(scrollYProgress, [0, 1], ["0%", "-15%"]);
  const heroOpacity = useTransform(scrollYProgress, [0, 0.85], [1, 0.5]);

  return (
    <section ref={ref} data-testid="hero-section" className="relative min-h-[100vh] pt-28 md:pt-36 overflow-hidden">
      {/* Soft cream BG */}
      <motion.div style={{ y: yBg }} className="absolute inset-0 -z-10">
        <div className="absolute inset-0 bg-gradient-to-b from-ab-bg via-[#EFEDE6] to-ab-bg" />
        <div className="absolute -top-32 -right-32 w-[600px] h-[600px] rounded-full bg-ab-accent/10 blur-3xl" />
        <div className="absolute top-1/3 -left-40 w-[480px] h-[480px] rounded-full bg-ab-primary/10 blur-3xl" />
      </motion.div>

      <div className="ab-container grid grid-cols-1 lg:grid-cols-12 gap-10 items-start relative">
        {/* Left: text */}
        <motion.div style={{ y: titleY, opacity: heroOpacity }} className="lg:col-span-7 pt-6 md:pt-12">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            className="inline-flex items-center gap-2 mb-8 px-4 py-1.5 rounded-full bg-white border border-ab-border"
            data-testid="hero-eyebrow"
          >
            <span className="ab-pulse-dot" />
            <span className="ab-eyebrow text-ab-primary">Hospital OS · v3</span>
            <span className="text-ab-muted text-xs">ABDM compliant · CERT-IN audited</span>
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.05 }}
            className="ab-h1 text-[14vw] sm:text-7xl md:text-8xl lg:text-[8.4rem] xl:text-[9.5rem] text-ab-primary"
          >
            The hospital
            <span className="block italic font-light text-ab-text">that <span className="text-ab-accent">breathes</span></span>
            <span className="block">with you.</span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.2 }}
            className="mt-8 text-lg md:text-xl text-ab-muted max-w-xl leading-relaxed"
          >
            PranaOs is the <span className="text-ab-text">unifier OS</span> for OPD, IPD, billing, pharmacy and the floor — so every department runs on a single, common rhythm.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.35 }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <a href="#contact" data-testid="hero-cta-btn" className="ab-pill-btn group">
              Book a 30-min demo
              <ArrowUpRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </a>
            <a href="#modules" data-testid="hero-secondary-cta" className="ab-pill-btn-ghost">
              See modules <Sparkles className="w-4 h-4" />
            </a>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 0.6 }}
            className="mt-14 flex flex-wrap items-center gap-x-10 gap-y-4 text-sm text-ab-muted"
          >
            <span className="flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-ab-accent" /> ABDM verified</span>
            <span className="flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-ab-accent" /> CERT-IN empaneled</span>
            <span className="flex items-center gap-2"><Activity className="w-4 h-4 text-ab-accent" /> 99.95% uptime</span>
          </motion.div>
        </motion.div>

        {/* Right: parallax cinematic stack */}
        <div className="lg:col-span-5 relative h-[480px] sm:h-[560px] lg:h-[640px] mt-4 lg:mt-0">
          <motion.div
            style={{ y: yDoctor }}
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1.1 }}
            className="absolute top-0 right-0 w-[78%] h-[58%] overflow-hidden rounded-[28px] border border-white shadow-[0_30px_60px_-30px_rgba(13,64,53,0.35)]"
          >
            <img src={HERO_DOCTOR} alt="Doctor with tablet" className="w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/15 via-transparent to-transparent" />
          </motion.div>

          <motion.div
            style={{ y: yWard }}
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 1.1, delay: 0.15 }}
            className="absolute bottom-0 left-0 w-[64%] h-[52%] overflow-hidden rounded-[24px] border border-white shadow-[0_30px_60px_-30px_rgba(13,64,53,0.35)]"
          >
            <img src={HERO_WARD} alt="Modern ward equipment" className="w-full h-full object-cover" />
          </motion.div>

          {/* Floating UI chip 1 - vitals */}
          <motion.div
            style={{ y: yChip1 }}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.5 }}
            className="absolute top-6 left-2 sm:left-6 bg-white border border-ab-border rounded-2xl p-4 shadow-xl w-[200px]"
          >
            <p className="ab-eyebrow text-ab-muted">Bed 204 · Vitals</p>
            <div className="mt-2 flex items-end gap-2">
              <span className="font-serif text-3xl text-ab-primary leading-none">98.4</span>
              <span className="text-xs text-ab-muted mb-1">°F</span>
            </div>
            <div className="mt-3 grid grid-cols-3 gap-2 text-[10px]">
              <div className="bg-ab-bg rounded-lg p-2"><p className="text-ab-muted">HR</p><p className="font-medium">76</p></div>
              <div className="bg-ab-bg rounded-lg p-2"><p className="text-ab-muted">SpO₂</p><p className="font-medium">98%</p></div>
              <div className="bg-ab-bg rounded-lg p-2"><p className="text-ab-muted">BP</p><p className="font-medium">118/76</p></div>
            </div>
          </motion.div>

          {/* Floating UI chip 2 - tokens */}
          <motion.div
            style={{ y: yChip2 }}
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.7 }}
            className="absolute -bottom-2 right-0 sm:-bottom-6 sm:right-2 bg-ab-primary text-white rounded-2xl p-5 shadow-2xl w-[230px]"
          >
            <p className="ab-eyebrow text-white/60">OPD · Live tokens</p>
            <div className="mt-3 flex items-baseline gap-2">
              <span className="font-serif text-5xl leading-none">A-32</span>
              <span className="text-white/70 text-xs">now</span>
            </div>
            <div className="mt-4 flex items-center gap-2">
              <span className="ab-pulse-dot bg-white" style={{ background: "#69E2C4" }}></span>
              <span className="text-xs text-white/80">Avg wait · 6m 14s</span>
            </div>
          </motion.div>

          {/* Floating UI chip 3 - bed map */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.9, delay: 0.9 }}
            className="absolute top-1/2 -translate-y-1/2 right-2 lg:right-[-30px] bg-white/90 backdrop-blur border border-ab-border rounded-xl p-3 shadow-xl"
          >
            <p className="ab-eyebrow text-ab-muted mb-2">Ward 3 · ICU</p>
            <div className="grid grid-cols-6 gap-1">
              {[...Array(18)].map((_, i) => (
                <span key={i} className={`w-3 h-3 rounded-sm ${i % 5 === 0 ? "bg-ab-accent" : i % 7 === 0 ? "bg-ab-border" : "bg-ab-primary"}`} />
              ))}
            </div>
          </motion.div>
        </div>
      </div>

      {/* Scroll cue */}
      <div className="ab-container mt-20 md:mt-28">
        <div className="flex items-center gap-4">
          <span className="ab-eyebrow text-ab-muted">Scroll</span>
          <div className="ab-shimmer-line h-px flex-1" />
          <span className="ab-eyebrow text-ab-muted">The OS in motion</span>
        </div>
      </div>
    </section>
  );
}
